import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  RefreshCw, 
  RotateCcw, 
  Eye, 
  Target, 
  Trophy, 
  Building, 
  Mail, 
  DollarSign, 
  Calendar,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { api } from '../../../utils/api';
import { formatCurrency } from '../../../utils/currency';

const DeletedRecords = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadsPagination, setLeadsPagination] = useState({});
  const [opportunitiesPagination, setOpportunitiesPagination] = useState({});

    // Fetch deleted leads
  const fetchDeletedLeads = async (page = 1) => {
    try {
      const data = await api.get(`/leads/deleted?page=${page}&limit=20`);
      setLeads(data.leads || []);
      setLeadsPagination(data.pagination || {});
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch deleted opportunities
  const fetchDeletedOpportunities = async (page = 1) => {
    try {
      const response = await fetch(`/api/opportunities/deleted?page=${page}&limit=20`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch deleted opportunities');
      }
      
      const data = await response.json();
      setOpportunities(data.opportunities || []);
      setOpportunitiesPagination(data.pagination || {});
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch data based on active tab
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === 'leads') {
        await fetchDeletedLeads();
      } else {
        await fetchDeletedOpportunities();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Handle restore lead
  const handleRestoreLead = async (leadId) => {
    try {
      await api.patch(`/leads/${leadId}/restore`);
      fetchData(); // Refresh data
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle restore opportunity
  const handleRestoreOpportunity = async (opportunityId) => {
    try {
      const response = await fetch(`/api/opportunities/${opportunityId}/restore`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to restore opportunity');
      }

      fetchData(); // Refresh data
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle permanent delete
  const handlePermanentDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to permanently delete this ${type}? This action cannot be undone.`)) {
      return;
    }

    try {
      const endpoint = type === 'lead' ? `/api/leads/${id}` : `/api/opportunities/${id}`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to permanently delete ${type}`);
      }

      fetchData(); // Refresh data
    } catch (err) {
      setError(err.message);
    }
  };

  // Helper function to format currency (now using utility)
  const formatCurrencyLocal = (amount, currency = 'THB') => {
    return formatCurrency(amount, currency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Closed Won': return 'bg-green-100 text-green-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      case 'Negotiation': return 'bg-orange-100 text-orange-800';
      case 'Proposal': return 'bg-yellow-100 text-yellow-800';
      case 'Qualified': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Trash2 className="h-6 w-6 mr-2 text-red-600" />
            {t('deletedRecords.title', 'Deleted Records')}
          </h1>
          <p className="text-gray-600">{t('deletedRecords.subtitle', 'Manage and restore deleted leads and opportunities')}</p>
        </div>
        <button 
          onClick={fetchData}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          <span>{t('common.refresh', 'Refresh')}</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('leads')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'leads'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Target className="h-4 w-4 inline mr-2" />
            {t('deletedRecords.leads', 'Leads')} ({leadsPagination.total || 0})
          </button>
          <button
            onClick={() => setActiveTab('opportunities')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'opportunities'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Trophy className="h-4 w-4 inline mr-2" />
            {t('deletedRecords.opportunities', 'Opportunities')} ({opportunitiesPagination.total || 0})
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'leads' ? (
        <div className="space-y-4">
          {leads.length === 0 ? (
            <div className="text-center py-12">
              <Trash2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('deletedRecords.noDeletedLeads', 'No deleted leads')}
              </h3>
              <p className="text-gray-500">
                {t('deletedRecords.noDeletedLeadsDesc', 'There are no deleted leads to restore.')}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('leads.title')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('leads.contact')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('leads.company')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('leads.priority')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('leads.budget')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.deletedAt', 'Deleted At')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.actions', 'Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leads.map((lead) => (
                      <tr key={lead.LeadID} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {lead.Title || lead.ContactName || `Lead #${lead.LeadID}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            {lead.Description && lead.Description.substring(0, 50)}...
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{lead.ContactName || '-'}</div>
                          <div className="text-sm text-gray-500">{lead.ContactEmail || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{lead.CompanyName || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(lead.Priority)}`}>
                            {lead.Priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrencyLocal(lead.EstimatedBudget, lead.Currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(lead.UpdatedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleRestoreLead(lead.LeadID)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="Restore"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            {t('common.restore', 'Restore')}
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(lead.LeadID, 'lead')}
                            className="text-red-600 hover:text-red-900 flex items-center"
                            title="Permanently Delete"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {t('common.permanentDelete', 'Delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {opportunities.length === 0 ? (
            <div className="text-center py-12">
              <Trash2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('deletedRecords.noDeletedOpportunities', 'No deleted opportunities')}
              </h3>
              <p className="text-gray-500">
                {t('deletedRecords.noDeletedOpportunitiesDesc', 'There are no deleted opportunities to restore.')}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('opportunities.title')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('opportunities.customer')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('opportunities.value')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('opportunities.stage')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('opportunities.priority')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.deletedAt', 'Deleted At')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.actions', 'Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {opportunities.map((opportunity) => (
                      <tr key={opportunity.OpportunityID} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {opportunity.OpportunityName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {opportunity.Description && opportunity.Description.substring(0, 50)}...
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{opportunity.CustomerName || '-'}</div>
                          <div className="text-sm text-gray-500">{opportunity.ContactName || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(opportunity.Value, opportunity.Currency)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opportunity.Stage)}`}>
                            {opportunity.Stage}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(opportunity.Priority)}`}>
                            {opportunity.Priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(opportunity.UpdatedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleRestoreOpportunity(opportunity.OpportunityID)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="Restore"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            {t('common.restore', 'Restore')}
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(opportunity.OpportunityID, 'opportunity')}
                            className="text-red-600 hover:text-red-900 flex items-center"
                            title="Permanently Delete"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {t('common.permanentDelete', 'Delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeletedRecords; 